import {
  Radio,
  Input,
  Show,
  RadioSize,
  TruncateWithTooltip
} from "shared/components";
import { cx, formatToCurrency } from "shared/utils";
import { Money } from "shared/types";
import { useAuthContext } from "providers/Auth";
import { useQuery } from "@tanstack/react-query";
import { Dispatch, SetStateAction } from "react";
import api from "api/landlord/address";
import { PreviewApplicationRentByUnit } from "types/application";

type RentOption = "use_current" | "use_listed" | "manual";

interface SetRentAmountOptionsProps {
  currentApplicationRents: RentAmountData & {
    housing_assistance_voucher_amount?: Money;
    pet_rent_amount?: Money;
  };
  applicationId: string;
  unitId?: number;
  unitName: string;
  rentChangeType: RentOption;
  setRentChangeType: (rentChangeType: RentOption) => void;
  manualRent?: RentAmountData;
  setManualRent: Dispatch<SetStateAction<RentAmountData | undefined>>;
}

export interface RentAmountData {
  monthly_rent_amount?: Money;
  rent_amount?: Money;
  home_rent_amount?: Money;
  site_rent_amount?: Money;
}

interface RentOptionItemProps {
  option: RentOption;
  label: string;
  rentAmount: RentAmountData & {
    housing_assistance_voucher_amount?: Money;
    pet_rent_amount?: Money;
  };
  description?: string;
  isChecked: boolean;
  onChange: (option: RentOption) => void;
}

const RentOptionItem = ({
  option,
  label,
  rentAmount,
  description,
  isChecked,
  onChange
}: RentOptionItemProps) => {
  const { pms } = useAuthContext();

  return (
    <div className="p-6 mb-4 border border-alpha-100 rounded-md bg-white">
      <div className="flex items-center justify-between">
        <Radio
          id={`rent-option-${option}`}
          checked={isChecked}
          onChange={() => onChange(option)}
          label={label}
          subLabelText={description}
          name="rent-option"
          size={RadioSize.LG}
        />
        <div className="flex items-center gap-4 text-sm text-content-300">
          {pms.isManageAmerica ? (
            <>
              <div className="space-y-2">
                <p className="text-xs">Home rent</p>
                <p className="font-medium">
                  {formatToCurrency(rentAmount.home_rent_amount || null)}
                </p>
              </div>
              <span className="text-content-1000 font-medium">+</span>
              <div className="space-y-2">
                <p className="text-xs">Site rent</p>
                <p className="font-medium">
                  {formatToCurrency(rentAmount.site_rent_amount || null)}
                </p>
              </div>
            </>
          ) : (
            <div className="space-y-2">
              <p className="text-xs">Rent amount</p>
              <p className="font-medium">
                {formatToCurrency(rentAmount.rent_amount || null)}
              </p>
            </div>
          )}

          <span className="text-content-1000 font-medium">-</span>
          <div className="space-y-2 min-w-[60px]">
            <p className="text-xs">Assistance</p>
            <p className="font-medium">
              {formatToCurrency({
                cents: rentAmount.housing_assistance_voucher_amount?.cents || 0,
                currency: "USD"
              })}
            </p>
          </div>

          <span className="text-content-1000 font-medium">+</span>
          <div className="space-y-2 min-w-[35px]">
            <p className="text-xs">Pet</p>
            <p className="font-medium">
              {formatToCurrency({
                cents: rentAmount.pet_rent_amount?.cents || 0,
                currency: "USD"
              })}
            </p>
          </div>

          <span className="text-content-1000 font-medium">=</span>
          <div className="space-y-2 min-w-[53px] text-right">
            <p className="text-xs">Total rent</p>
            <p className="text-content-1000 font-medium">
              {formatToCurrency(rentAmount.monthly_rent_amount || null)}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

interface ManualRentInputProps {
  isChecked: boolean;
  onChange: (option: RentOption) => void;
  manualRent?: RentAmountData;
  onManualRentChange: (field: keyof RentAmountData, value: string) => void;
  calculateManualTotalRentCents: () => number;
  listedApplicationRentByUnit?: PreviewApplicationRentByUnit;
}

const ManualRentInput = ({
  isChecked,
  onChange,
  manualRent,
  onManualRentChange,
  calculateManualTotalRentCents,
  listedApplicationRentByUnit
}: ManualRentInputProps) => {
  const { pms } = useAuthContext();

  return (
    <div className="p-6 border border-alpha-100 rounded-md bg-white">
      <div className="mb-6">
        <Radio
          id="rent-option-manual"
          checked={isChecked}
          onChange={() => onChange("manual")}
          label="Set rent amount manually"
          name="rent-option"
          size={RadioSize.LG}
        />
      </div>
      <div className="flex items-center gap-4 text-sm">
        <div className="flex gap-4 items-center">
          {pms.isManageAmerica ? (
            <>
              <div>
                <Input
                  id="home-rent-manual-input"
                  className="w-[215px]"
                  type="money"
                  value={
                    manualRent?.home_rent_amount?.cents !== undefined
                      ? (manualRent.home_rent_amount.cents || 0) / 100
                      : ""
                  }
                  onChange={(e) =>
                    onManualRentChange("home_rent_amount", e.target.value)
                  }
                  min={0}
                  max={99999}
                  label="Home rent"
                  required
                  readOnly={!isChecked}
                  hideErrorMessage
                />
              </div>
              <span className="text-content-1000 font-medium pt-6">+</span>
              <div>
                <Input
                  id="site-rent-manual-input"
                  className="w-[215px]"
                  type="money"
                  value={
                    manualRent?.site_rent_amount?.cents !== undefined
                      ? (manualRent.site_rent_amount.cents || 0) / 100
                      : ""
                  }
                  onChange={(e) =>
                    onManualRentChange("site_rent_amount", e.target.value)
                  }
                  min={0}
                  max={99999}
                  label="Site rent"
                  required
                  readOnly={!isChecked}
                  hideErrorMessage
                />
              </div>
            </>
          ) : (
            <div>
              <Input
                id="application-rent-manual-input"
                className="!w-[218px]"
                type="money"
                value={
                  manualRent?.rent_amount?.cents !== undefined
                    ? (manualRent.rent_amount.cents || 0) / 100
                    : ""
                }
                min={0}
                max={99999}
                onChange={(e) =>
                  onManualRentChange("rent_amount", e.target.value)
                }
                label="Rent amount"
                required
                readOnly={!isChecked}
                hideErrorMessage
              />
            </div>
          )}
        </div>
        <div className="flex-1 flex gap-4 items-center justify-between pt-6">
          <span className="text-content-1000 font-medium">-</span>
          <div
            className={cx(
              "space-y-2 text-content-300",
              pms.isManageAmerica ? "min-w-[60px]" : "min-w-[85px]"
            )}
          >
            <p className="text-xs">Assistance</p>
            <p className="font-medium">
              {formatToCurrency(
                listedApplicationRentByUnit?.housing_assistance_voucher_amount ||
                  null
              )}
            </p>
          </div>
          <span className="text-content-1000 font-medium">+</span>
          <div
            className={cx(
              "space-y-2 text-content-300",
              pms.isManageAmerica ? "min-w-[35px]" : "min-w-[85px]"
            )}
          >
            <p className="text-xs">Pet</p>
            <p className="font-medium">
              {formatToCurrency(
                listedApplicationRentByUnit?.pet_rent_amount || null
              )}
            </p>
          </div>
          <span className="text-content-1000 font-medium">=</span>
          <div
            className={cx(
              "space-y-2",
              pms.isManageAmerica ? "min-w-[60px]" : "min-w-[90px]"
            )}
          >
            <p className="text-xs text-content-300">Application rent</p>
            <p className="text-content-1000 font-medium text-end">
              <TruncateWithTooltip
                content={formatToCurrency({
                  cents: calculateManualTotalRentCents(),
                  currency: "USD"
                })}
                maxWidth="90px"
                tooltipClassName="z-[9999]"
              />
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export const SetRentAmountOptions = ({
  applicationId,
  unitId,
  unitName,
  currentApplicationRents,
  rentChangeType,
  setRentChangeType,
  manualRent,
  setManualRent
}: SetRentAmountOptionsProps) => {
  const { pms, permissions } = useAuthContext();

  const { data: listedApplicationRentByUnit } =
    useQuery<PreviewApplicationRentByUnit>({
      queryKey: ["PREVIEW_APPLICATION_RENT_BY_UNIT", applicationId, unitId],
      queryFn: () =>
        api.getPreviewApplicationRentByUnit(applicationId, unitId || 0)
    });

  const calculateManualTotalRentCents = () => {
    const baseRent = pms.isManageAmerica
      ? (manualRent?.home_rent_amount?.cents || 0) +
        (manualRent?.site_rent_amount?.cents || 0)
      : manualRent?.rent_amount?.cents || 0;
    const petRent = listedApplicationRentByUnit?.pet_rent_amount?.cents || 0;
    const assistanceRent =
      listedApplicationRentByUnit?.housing_assistance_voucher_amount?.cents ||
      0;
    const result = baseRent + petRent - assistanceRent;
    if (result < 0) return 0;
    return result;
  };

  const handleManualRentChange = (
    field: keyof RentAmountData,
    value: string
  ) => {
    if (value === "") {
      setManualRent((prev) => ({
        ...prev,
        [field]: undefined
      }));
      return;
    }
    const cents = parseFloat(value) * 100 || 0;
    setManualRent((prev) => ({
      ...prev,
      [field]: { cents, currency: "USD" }
    }));
  };

  return (
    <div className="px-6 py-8 bg-background-900 max-h-[500px] overflow-y-auto">
      <RentOptionItem
        option="use_current"
        label="Use current rent"
        rentAmount={currentApplicationRents}
        isChecked={rentChangeType === "use_current"}
        onChange={setRentChangeType}
      />

      <RentOptionItem
        option="use_listed"
        label="Use listed rent"
        rentAmount={listedApplicationRentByUnit || {}}
        description={`For Unit ${unitName}`}
        isChecked={rentChangeType === "use_listed"}
        onChange={setRentChangeType}
      />

      <Show if={permissions.canEditApplicationRent}>
        <ManualRentInput
          isChecked={rentChangeType === "manual"}
          onChange={setRentChangeType}
          manualRent={manualRent}
          onManualRentChange={handleManualRentChange}
          calculateManualTotalRentCents={calculateManualTotalRentCents}
          listedApplicationRentByUnit={listedApplicationRentByUnit}
        />
      </Show>
    </div>
  );
};
